/**
 * DTR kit builder — visual front end for a Simple Bundles "Infinite options" bundle. (Focal port)
 *
 * Source of truth for what gets added to cart is Simple Bundles' own native <select> controls (rendered by its app
 * block inside this section). This element never writes cart properties itself: it sets the native select value and
 * dispatches `change`, which is what Simple Bundles' io.js listens to in order to fill the product form's hidden
 * properties. Before submission it verifies the form data matches the visual selection.
 */

// Simple Bundles joins values with " ++ " inside an option group and " <> " between groups.
const SELECTION_SEPARATOR = / (?:<>|\+\+) /;

// Collage quadrant images: sized CDN renditions, never the same widths requested for the full-size swatch image.
const COLLAGE_WIDTHS = [300, 450, 600, 800];
const COLLAGE_SIZES = '(min-width: 1000px) 22vw, 46vw';

class DtrKitBuilder extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.form = document.getElementById(this.getAttribute('form'));
    const configElement = this.querySelector('[data-kit-config]');
    const nativeRoot = this.querySelector('#simple-bundles-io-options');

    if (!this.form || !configElement) return; // Section is in its merchant setup state (no bundle data)

    this.config = JSON.parse(configElement.textContent);
    this.strings = this.config.strings || {};
    this.stepConfigByOptionName = new Map(
      (this.config.steps || []).filter((step) => step.optionName).map((step) => [step.optionName, step])
    );

    // Note: form.id would return the form's <input name="id">, hence reading the attribute.
    this.submitButtons = Array.from(document.querySelectorAll(`button[type="submit"][form="${cssEscape(this.getAttribute('form'))}"]`));
    this.messageElement = this.querySelector('[data-kit-message]');
    this.formatter = createMoneyFormatter();

    // Direct references, grabbed once: drawer-content[append-body] moves itself to <body> as soon as it is
    // upgraded (usually before this element's own connectedCallback runs), so it stops being a descendant of
    // this element — this.querySelector would fail to find it. document.getElementById always finds it, wherever
    // it now lives.
    this.detailsDrawer = document.getElementById(`${this.id}-details`);
    this.detailsDrawerTitle = this.detailsDrawer?.querySelector('[data-drawer-title]') || null;
    this.detailsDrawerBody = this.detailsDrawer?.querySelector('[data-drawer-body]') || null;
    this.sizeDrawer = document.getElementById(`${this.id}-size`);
    this.sizeDrawerTitle = this.sizeDrawer?.querySelector('[data-drawer-title]') || null;
    this.sizeDrawerBody = this.sizeDrawer?.querySelector('[data-drawer-body]') || null;

    // One storefront fetch per product handle per page load, however many steps/drawers reference that product.
    this._productDocCache = new Map();
    this._preloadedImageUrls = new Set();

    const collageImages = {};
    this.querySelectorAll('[data-collage-quadrant]').forEach((quadrant) => {
      collageImages[quadrant.dataset.collageQuadrant] = quadrant.querySelector('[data-collage-image]');
    });

    this.steps = Array.from(this.querySelectorAll('[data-kit-step]')).map((element) => {
      const stepConfig = this.stepConfigByOptionName.get(element.dataset.optionName) || {};

      return {
        element,
        optionName: element.dataset.optionName,
        title: element.dataset.stepTitle,
        chooseMessage: stepConfig.chooseMessage || '',
        sizeMessage: stepConfig.sizeMessage || '',
        collageImage: collageImages[element.dataset.collagePosition] || null,
        select: nativeRoot?.querySelector(`select[name="properties[${cssEscape(element.dataset.optionName)}]"]`),
        swatchesContainer: element.querySelector('[data-kit-swatches]'),
        nameElement: element.querySelector('[data-step-name]'),
        priceDiffElement: element.querySelector('[data-step-price-diff]'),
        linksElement: element.querySelector('[data-step-links]'),
        detailsButton: element.querySelector('[data-step-details-button]'),
        sizeButton: element.querySelector('[data-step-size-button]'),
        productId: null,
        label: null,
        previewing: false,
        collageLoadToken: null
      };
    });

    const missing = this.steps.filter((step) => !step.select);

    if (!nativeRoot || missing.length > 0) {
      console.error('[dtr-kit] Simple Bundles controls not found', { nativeRoot, missing: missing.map((step) => step.optionName) });
      this._broken = true;
      this._render();
      return;
    }

    this._applyAvailability();
    this._restore();
    this._selectDefaults();
    this._render();
    this._loadMissingImages();

    this.addEventListener('change', this._onChange.bind(this));
    this.addEventListener('submit', this._onSubmit.bind(this), { capture: true });
    this.form.addEventListener('variant:added', this._onAdded.bind(this));
    this.form.addEventListener('cart-notification:show', this._syncButtons.bind(this));

    this.steps.forEach((step) => {
      if (step.swatchesContainer) {
        step.swatchesContainer.addEventListener('mouseover', (event) => {
          const swatch = event.target.closest('.dtr-kit-swatch');
          if (swatch) {
            this._previewStepName(step, swatch);
            this._preloadSwatchImage(swatch);
          }
        });

        step.swatchesContainer.addEventListener('mouseleave', () => this._endPreview(step));

        step.swatchesContainer.addEventListener('focusin', (event) => {
          const swatch = event.target.closest('.dtr-kit-swatch');
          if (swatch) {
            this._previewStepName(step, swatch);
            this._preloadSwatchImage(swatch);
          }
        });

        step.swatchesContainer.addEventListener('focusout', (event) => {
          if (!step.swatchesContainer.contains(event.relatedTarget)) this._endPreview(step);
        });
      }

      if (step.detailsButton) {
        step.detailsButton.addEventListener('click', () => this._openDetailsDrawer(step));
        step.detailsButton.addEventListener('pointerenter', () => this._prefetchStepProduct(step));
        step.detailsButton.addEventListener('focus', () => this._prefetchStepProduct(step));
      }

      if (step.sizeButton) {
        step.sizeButton.addEventListener('click', () => this._openSizeDrawer(step));
        step.sizeButton.addEventListener('pointerenter', () => this._prefetchStepProduct(step));
        step.sizeButton.addEventListener('focus', () => this._prefetchStepProduct(step));
      }
    });

    // Back/forward cache: Simple Bundles re-runs nothing, so re-assert our selection onto the native controls.
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        this.steps.forEach((step) => step.label && this._writeNative(step, step.label));
        this._render();
      }
    });
  }

  /* ------------------------------------------------------------------------------------------------------------------
   * Native Simple Bundles bridge
   * ---------------------------------------------------------------------------------------------------------------- */

  _nativeOption(step, label) {
    // Compare values directly instead of building a selector: labels contain quotes and apostrophes.
    return Array.from(step.select.options).find((option) => option.value === label && option.value !== '') || null;
  }

  _isAvailable(step, label) {
    const option = this._nativeOption(step, label);
    return Boolean(option && !option.disabled);
  }

  _priceDiff(step, label) {
    return parseFloat(this._nativeOption(step, label)?.dataset.priceDiff) || 0;
  }

  _writeNative(step, label) {
    const option = this._nativeOption(step, label);

    if (!option || option.disabled) return false;

    // Always dispatch, even if the value is unchanged: io.js only syncs the form on change.
    step.select.value = label;
    step.select.dispatchEvent(new Event('change', { bubbles: true }));

    return step.select.value === label;
  }

  _clearNative(step) {
    // Back to Simple Bundles' empty placeholder, so the form never carries a size the shopper did not (or no longer) chose.
    if (step.select.value === '') return;

    step.select.value = '';
    step.select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  _formMatchesSelection() {
    const formData = new FormData(this.form);
    const bundleSelection = formData.get('properties[_bundle_selection]') || '';

    return this.steps.every((step) => {
      return step.select.value === step.label
        && formData.getAll(`properties[${step.optionName}]`).length === 1
        && formData.get(`properties[${step.optionName}]`) === step.label
        && bundleSelection.split(SELECTION_SEPARATOR).includes(step.label);
    });
  }

  /* ------------------------------------------------------------------------------------------------------------------
   * Selection
   * ---------------------------------------------------------------------------------------------------------------- */

  _onChange(event) {
    const input = event.target;

    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

    const step = this.steps.find((candidate) => candidate.element.contains(input));

    if (!step) return;

    if (input.closest('.dtr-kit-swatch')) {
      this._selectProduct(step, input.value);
    } else {
      this._selectSize(step, input.value);
    }

    this._save();
    this._render();
  }

  _selectProduct(step, productId) {
    if (step.productId === productId) return;

    const previousSizeTitle = step.label ? this._sizeInput(step, step.label)?.dataset.sizeTitle : null;

    step.productId = productId;
    step.label = null;

    const input = this._swatchInput(step, productId);
    if (input) input.checked = true;

    step.element.querySelectorAll('[data-sizes-for] input').forEach((candidate) => (candidate.checked = false));

    // Keep the shopper's size when switching design, if that exact size exists and is available.
    if (previousSizeTitle) {
      const sameSize = Array.from(this._sizesGroup(step)?.querySelectorAll('input') || [])
        .find((candidate) => candidate.dataset.sizeTitle === previousSizeTitle && !candidate.disabled);

      if (sameSize) this._selectSize(step, sameSize.value);
    }

    if (!step.label) this._clearNative(step);
  }

  _selectSize(step, label) {
    const input = this._sizeInput(step, label);

    if (!input || !this._writeNative(step, label)) {
      if (input) input.checked = false;
      step.label = null;
      this._clearNative(step);
      this._setMessage(this.strings.sizeSoldOutMessage || '');
      return;
    }

    input.checked = true;
    step.label = label;
  }

  // Default selection (no saved/valid state): the first available (not sold-out) design, with no size chosen.
  _selectDefaults() {
    this.steps.forEach((step) => {
      if (step.productId) return;

      const swatch = Array.from(step.element.querySelectorAll('.dtr-kit-swatch'))
        .find((candidate) => !candidate.querySelector('input').disabled);

      if (swatch) this._selectProduct(step, swatch.dataset.productId);
    });
  }

  _swatchLabel(step, productId) {
    return step.element.querySelector(`.dtr-kit-swatch[data-product-id="${productId}"]`);
  }

  _swatchInput(step, productId) {
    return this._swatchLabel(step, productId)?.querySelector('input') || null;
  }

  _sizesGroup(step) {
    return step.element.querySelector(`[data-sizes-for="${step.productId}"]`);
  }

  _sizeInput(step, label) {
    return Array.from(step.element.querySelectorAll('[data-sizes-for] input')).find((input) => input.value === label);
  }

  /* ------------------------------------------------------------------------------------------------------------------
   * Availability & prices (read from the native options Simple Bundles rendered)
   * ---------------------------------------------------------------------------------------------------------------- */

  _applyAvailability() {
    const threshold = this.config.lowStockThreshold || 0;

    this.steps.forEach((step) => {
      step.element.querySelectorAll('.dtr-kit-swatch').forEach((swatch) => {
        const sizeInputs = Array.from(step.element.querySelectorAll(`[data-sizes-for="${swatch.dataset.productId}"] input`));
        let available = false;

        sizeInputs.forEach((input) => {
          const option = this._nativeOption(step, input.value);
          const isAvailable = Boolean(option && !option.disabled);

          input.disabled = !isAvailable;
          input.closest('.dtr-kit-size').classList.toggle('is-unavailable', input.disabled);

          const lowStockElement = input.closest('.dtr-kit-size').querySelector('[data-low-stock]');
          if (lowStockElement) {
            const inventory = parseInt(option?.dataset.inventory, 10);
            const showQuantity = isAvailable && threshold > 0 && Number.isFinite(inventory) && inventory >= 1 && inventory <= threshold;

            if (showQuantity) {
              lowStockElement.textContent = (this.strings.lowStockMessage || '').replace('{count}', inventory);
              lowStockElement.hidden = false;
            } else {
              lowStockElement.hidden = true;
              lowStockElement.textContent = '';
            }
          }

          if (isAvailable) available = true;
        });

        const swatchInput = swatch.querySelector('input');

        swatch.classList.toggle('is-sold-out', !available);
        swatchInput.disabled = !available;
      });
    });
  }

  _formatDiff(diff) {
    return this.formatter(Math.round(diff * currencyRate() * 100));
  }

  /* ------------------------------------------------------------------------------------------------------------------
   * Rendering
   * ---------------------------------------------------------------------------------------------------------------- */

  _render() {
    const complete = !this._broken && this.steps.every((step) => step.label);

    this.steps.forEach((step) => {
      step.element.querySelectorAll('.dtr-kit-swatch').forEach((swatch) => {
        swatch.classList.toggle('is-selected', swatch.dataset.productId === step.productId);
      });

      step.element.querySelectorAll('[data-sizes-for]').forEach((group) => {
        group.hidden = group.dataset.sizesFor !== step.productId;
      });

      this._updateStepMeta(step);
      this._updateCollageImage(step);
    });

    this._updatePrice(complete);

    this.submitButtons.forEach((button) => (button.disabled = !complete));

    if (this._broken) {
      this._setMessage(this.strings.brokenMessage || '');
    } else if (!complete) {
      const next = this.steps.find((step) => !step.label);
      this._setMessage(next.productId ? next.sizeMessage : next.chooseMessage);
    } else {
      this._setMessage('');
    }
  }

  // Step heading name, price difference and details/size links for the currently selected design.
  _updateStepMeta(step) {
    const swatch = step.productId ? this._swatchLabel(step, step.productId) : null;

    if (!step.previewing) this._setStepName(step, swatch?.dataset.productName || '');

    if (step.priceDiffElement) {
      const diff = step.label ? this._priceDiff(step, step.label) : 0;

      if (diff > 0) {
        step.priceDiffElement.textContent = `+${this._formatDiff(diff)}`;
        step.priceDiffElement.hidden = false;
      } else {
        step.priceDiffElement.hidden = true;
      }
    }

    if (step.linksElement) {
      const linkable = Boolean(swatch && swatch.dataset.linkable === 'true' && swatch.dataset.handle);
      step.linksElement.hidden = !linkable;

      if (linkable) {
        const name = swatch.dataset.productName || '';
        if (step.detailsButton) step.detailsButton.setAttribute('aria-label', `${this.strings.productDetailsLabel || ''}: ${name}`);
        if (step.sizeButton) step.sizeButton.setAttribute('aria-label', `${this.strings.sizeFitLabel || ''}: ${name}`);
      }
    }
  }

  _setStepName(step, name) {
    if (step.nameElement) step.nameElement.textContent = name;
  }

  _previewStepName(step, swatch) {
    step.previewing = true;
    this._setStepName(step, swatch.dataset.productName || '');
  }

  _endPreview(step) {
    step.previewing = false;
    this._updateStepMeta(step);
  }

  // Collage quadrant image for the current selection, built from the swatch's data-image-large. Preloads the new
  // image off-DOM and waits for it to decode before swapping, so the collage never flashes a half-loaded image; a
  // per-step token guards against a slower, now-stale load winning over a newer selection.
  async _updateCollageImage(step) {
    const target = step.collageImage;

    if (!target) return;

    const swatch = step.productId ? this._swatchLabel(step, step.productId) : null;
    const base = swatch?.dataset.imageLarge;

    if (!base) {
      step.collageLoadToken = null;
      target.hidden = true;
      target.removeAttribute('src');
      target.removeAttribute('srcset');
      return;
    }

    const token = Symbol('collage-load');
    step.collageLoadToken = token;

    // Cell width varies with the step count — a lone last cell spans both columns — so the section stamps each
    // quadrant with its own sizes hint. Fall back to the two-up default when it is absent.
    const sizes = target.getAttribute('sizes') || COLLAGE_SIZES;

    const preload = new Image();
    applySrcset(preload, base, COLLAGE_WIDTHS, sizes);

    await waitForImage(preload);

    if (step.collageLoadToken !== token) return; // Selection changed again while this image was loading.

    applySrcset(target, base, COLLAGE_WIDTHS, sizes);
    target.alt = swatch?.dataset.productName || '';
    target.hidden = false;
  }

  // Warms the browser cache for a swatch's collage-sized image on hover/focus, before the shopper even clicks it.
  _preloadSwatchImage(swatch) {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    const base = swatch?.dataset.imageLarge;
    if (!base) return;

    let key;

    try {
      const url = new URL(base, window.location.href);
      url.searchParams.set('width', String(COLLAGE_WIDTHS[COLLAGE_WIDTHS.length - 1]));
      key = url.toString();
    } catch (error) {
      key = base;
    }

    if (this._preloadedImageUrls.has(key)) return;
    this._preloadedImageUrls.add(key);

    const image = new Image();
    applySrcset(image, base, COLLAGE_WIDTHS, COLLAGE_SIZES);
  }

  _updatePrice(complete) {
    const rate = currencyRate();

    // Kit price = bundle variant price (sum of the cheapest choice per option) + the selected options' price
    // differences. This mirrors how Simple Bundles prices the line in the cart.
    const kitPrice = this.config.basePrice + this.steps.reduce((sum, step) => {
      return sum + (step.label ? Math.round(this._priceDiff(step, step.label) * rate * 100) : 0);
    }, 0);

    const productsTotal = complete ? this.steps.reduce((sum, step) => {
      return sum + Math.round(parseFloat(this._sizeInput(step, step.label).dataset.retailPrice) * rate * 100);
    }, 0) : 0;

    const fromPrefix = complete ? '' : `${this.strings.fromLabel || ''} `;

    this.querySelectorAll('[data-kit-price]').forEach((element) => {
      element.textContent = `${fromPrefix}${this.formatter(kitPrice)}`;
    });

    const wasElement = this.querySelector('[data-products-total]');
    if (wasElement) {
      const showWas = complete && productsTotal > kitPrice;
      wasElement.hidden = !showWas;
      if (showWas) wasElement.textContent = this.formatter(productsTotal);
    }
  }

  _setMessage(text) {
    if (this.messageElement && this.messageElement.textContent !== text) {
      this.messageElement.textContent = text;
    }
  }

  // Re-applies the builder's own enabled/disabled state to the submit buttons. Focal's ProductForm unconditionally
  // clears `disabled`/`aria-busy` on every element in `this.form.elements` (which includes our external `form="…"`
  // buttons) once its own /cart/add.js request settles — regardless of whether the kit selection is still
  // complete — so this must re-run after `variant:added` and `cart-notification:show`.
  _syncButtons() {
    const complete = !this._broken && this.steps.every((step) => step.label);
    this.submitButtons.forEach((button) => (button.disabled = !complete));
  }

  /* ------------------------------------------------------------------------------------------------------------------
   * Product details / Size & fit drawers
   *
   * Two drawers are shared across every step (rather than one pair per step) and their content is refreshed from
   * the step's current product each time they open. The PDP is fetched once per handle per page load (cached as a
   * Promise so concurrent opens/prefetches share the same request) and parsed with DOMParser; everything pulled out
   * of it is rebuilt from scratch (sanitized) rather than injected as trusted HTML.
   * ---------------------------------------------------------------------------------------------------------------- */

  _prefetchStepProduct(step) {
    const swatch = step.productId ? this._swatchLabel(step, step.productId) : null;

    if (swatch && swatch.dataset.linkable === 'true' && swatch.dataset.handle) {
      this._fetchProductDocument(swatch.dataset.handle);
    }
  }

  _fetchProductDocument(handle) {
    if (!handle) return Promise.resolve(null);

    if (!this._productDocCache.has(handle)) {
      const promise = fetch(`${routesRoot()}products/${encodeURIComponent(handle)}`)
        .then((response) => (response.ok ? response.text() : Promise.reject(new Error(`HTTP ${response.status}`))))
        .then((html) => new DOMParser().parseFromString(html, 'text/html'))
        .catch(() => null);

      this._productDocCache.set(handle, promise);
    }

    return this._productDocCache.get(handle);
  }

  async _openDetailsDrawer(step) {
    const swatch = step.productId ? this._swatchLabel(step, step.productId) : null;

    if (!swatch || swatch.dataset.linkable !== 'true' || !swatch.dataset.handle) return;

    const productName = swatch.dataset.productName || '';
    const drawer = this.detailsDrawer;
    const titleElement = this.detailsDrawerTitle;
    const bodyElement = this.detailsDrawerBody;

    if (!drawer || !bodyElement) return;

    if (titleElement) titleElement.textContent = productName;
    bodyElement.replaceChildren(this._buildLoadingMessage(this.strings.loadingMessage || ''));

    const token = Symbol('details-load');
    this._detailsLoadToken = token;

    drawer.open = true;

    const doc = await this._fetchProductDocument(swatch.dataset.handle);

    if (this._detailsLoadToken !== token) return; // A different step's drawer request has since taken over.

    if (!doc) {
      bodyElement.replaceChildren(this._buildLoadingMessage(this.strings.unavailableInfoMessage || ''));
      return;
    }

    bodyElement.replaceChildren(...this._buildProductDetailsContent(doc));
  }

  async _openSizeDrawer(step) {
    const swatch = step.productId ? this._swatchLabel(step, step.productId) : null;

    if (!swatch || swatch.dataset.linkable !== 'true' || !swatch.dataset.handle) return;

    const drawer = this.sizeDrawer;
    const titleElement = this.sizeDrawerTitle;
    const bodyElement = this.sizeDrawerBody;

    if (!drawer || !bodyElement) return;

    if (titleElement) titleElement.textContent = this.strings.sizeFitLabel || '';
    bodyElement.replaceChildren(this._buildLoadingMessage(this.strings.loadingMessage || ''));

    const token = Symbol('size-load');
    this._sizeLoadToken = token;

    drawer.open = true;

    const doc = await this._fetchProductDocument(swatch.dataset.handle);

    if (this._sizeLoadToken !== token) return;

    if (!doc) {
      bodyElement.replaceChildren(this._buildLoadingMessage(this.strings.unavailableInfoMessage || ''));
      return;
    }

    const chart = doc.querySelector('drawer-content[id*="-size-chart-drawer"]');
    const chartContentSource = chart?.querySelector('.drawer__content .rte');

    if (!chart || !chartContentSource) {
      bodyElement.replaceChildren(this._buildLoadingMessage(this.strings.noSizeGuideMessage || ''));
      return;
    }

    if (titleElement) {
      const chartTitle = chart.querySelector('.drawer__title')?.textContent?.trim();
      titleElement.textContent = chartTitle || this.strings.sizeFitLabel || '';
    }

    const content = document.createElement('div');
    content.className = 'rte';
    content.appendChild(sanitizeFragment(chartContentSource.innerHTML));
    bodyElement.replaceChildren(content);
  }

  _buildLoadingMessage(text) {
    const message = document.createElement('p');
    message.className = 'text--subdued';
    message.textContent = text;
    return message;
  }

  // Rebuilds a small, sanitized subset of the fetched PDP: up to 4 gallery images and the description — never the
  // price, variant picker, buy buttons or quantity selector. Focal's product page has no accordion blocks, so
  // (unlike the Impact source) there is no separate accordion-rebuilding branch here.
  _buildProductDetailsContent(doc) {
    const nodes = [];

    const seenSrc = new Set();
    const images = Array.from(doc.querySelectorAll('.product__media-item img'))
      .filter((image) => {
        const src = image.getAttribute('src');
        if (!src || seenSrc.has(src)) return false;
        seenSrc.add(src);
        return true;
      })
      .slice(0, 4);

    if (images.length > 0) {
      const gallery = document.createElement('div');
      gallery.className = 'dtr-kit-drawer-gallery';

      images.forEach((sourceImage) => {
        const src = sourceImage.getAttribute('src');
        const image = document.createElement('img');
        image.loading = 'lazy';
        image.alt = sourceImage.getAttribute('alt') || '';
        applySrcset(image, src, [300, 450, 600], '(min-width: 700px) 25vw, 50vw');
        gallery.appendChild(image);
      });

      nodes.push(gallery);
    }

    const descriptionSource = doc.querySelector('.product-form__description');

    if (descriptionSource) {
      const description = document.createElement('div');
      description.className = 'rte';
      description.appendChild(sanitizeFragment(descriptionSource.innerHTML));
      nodes.push(description);
    }

    if (nodes.length === 0) {
      nodes.push(this._buildLoadingMessage(this.strings.unavailableInfoMessage || ''));
    }

    return nodes;
  }

  /* ------------------------------------------------------------------------------------------------------------------
   * Submission guard
   * ---------------------------------------------------------------------------------------------------------------- */

  _onSubmit(event) {
    if (event.target !== this.form) return;

    const block = (message) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      this._setMessage(message);
    };

    if (this._broken || !this.steps.every((step) => step.label)) {
      return block(this.strings.incompleteMessage || '');
    }

    if (!this._formMatchesSelection()) {
      // Re-sync once through the native path, then re-check.
      this.steps.forEach((step) => this._writeNative(step, step.label));

      if (!this._formMatchesSelection()) {
        console.error('[dtr-kit] Form properties do not match the selection', Object.fromEntries(new FormData(this.form)));
        return block(this.strings.syncErrorMessage || '');
      }
    }
  }

  _onAdded() {
    // The theme's mini-cart may not open (or may be disabled), so always confirm the add inside the builder itself.
    const link = document.createElement('a');
    link.href = `${routesRoot()}cart`;
    link.className = 'link';
    link.textContent = this.strings.viewCartLabel || '';

    this.messageElement.replaceChildren(`${this.strings.addedMessage || ''} `, link);

    this._syncButtons();
  }

  /* ------------------------------------------------------------------------------------------------------------------
   * Persistence (per-viewer convenience: survives refresh and returning from the cart)
   * ---------------------------------------------------------------------------------------------------------------- */

  _save() {
    const data = {};
    this.steps.forEach((step) => (data[step.optionName] = { productId: step.productId, label: step.label }));

    try {
      sessionStorage.setItem(this.config.storageKey, JSON.stringify(data));
    } catch (error) {
      // Storage unavailable (private mode…): selection simply won't persist.
    }
  }

  _restore() {
    // Browsers may restore radio states on reload; the saved state (validated against live availability) wins.
    this.querySelectorAll('.dtr-kit__steps input[type="radio"]').forEach((input) => (input.checked = false));

    let data = null;

    try {
      data = JSON.parse(sessionStorage.getItem(this.config.storageKey));
    } catch (error) {
      data = null;
    }

    this.steps.forEach((step) => {
      const saved = data?.[step.optionName];
      const swatchInput = saved?.productId ? this._swatchInput(step, saved.productId) : null;

      if (!swatchInput || swatchInput.disabled) return;

      this._selectProduct(step, saved.productId);

      const sizeInput = saved.label ? this._sizeInput(step, saved.label) : null;
      const sameProduct = sizeInput?.closest('[data-sizes-for]').dataset.sizesFor === saved.productId;

      if (sizeInput && sameProduct && !sizeInput.disabled) {
        this._selectSize(step, saved.label);
      }
    });

    // Simple Bundles pre-selects the first available option on load; neutralise every step the shopper hasn't completed.
    this.steps.forEach((step) => !step.label && this._clearNative(step));

    this._setMessage('');
  }

  /* ------------------------------------------------------------------------------------------------------------------
   * Images for products Liquid could not load (all_products is limited to 20 handles per page)
   * ---------------------------------------------------------------------------------------------------------------- */

  _loadMissingImages() {
    const pending = Array.from(this.querySelectorAll('img[data-image-pending]'));

    if (pending.length === 0) return;

    const load = async (image) => {
      const swatch = image.closest('.dtr-kit-swatch');

      try {
        const response = await fetch(`${routesRoot()}products/${encodeURIComponent(swatch.dataset.handle)}.js`);

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const product = await response.json();
        const src = product.featured_image;

        if (!src) throw new Error('No image');

        applySrcset(image, src, [120, 240], '56px');
        image.hidden = false;
        swatch.dataset.imageLarge = new URL(src, window.location.href).toString();
        swatch.dataset.linkable = 'true';
      } catch (error) {
        // Unpublished/draft components can't be read from the storefront: keep the neutral placeholder.
        swatch.classList.add('has-no-image');
      } finally {
        image.removeAttribute('data-image-pending');

        // If this swatch is the currently selected design for its step, refresh the bits that depend on its image/link.
        const step = this.steps.find((candidate) => candidate.productId === swatch.dataset.productId && candidate.element.contains(swatch));

        if (step) {
          this._updateStepMeta(step);
          this._updateCollageImage(step);
        }
      }
    };

    // The JSON requests are small and browser-cached; the images themselves are native lazy-loaded.
    const run = () => pending.forEach(load);
    'requestIdleCallback' in window ? window.requestIdleCallback(run, { timeout: 1500 }) : setTimeout(run, 200);
  }
}

function routesRoot() {
  return window.Shopify?.routes?.root || '/';
}

// Builds a responsive srcset/sizes/src from a single CDN image URL by overriding its `width` query param.
function applySrcset(image, baseUrl, widths, sizes) {
  try {
    const url = new URL(baseUrl, window.location.href);
    const withWidth = (width) => {
      url.searchParams.set('width', width);
      return `${url.toString()} ${width}w`;
    };

    image.srcset = widths.map(withWidth).join(', ');
    image.sizes = sizes;
    image.src = withWidth(widths[Math.floor(widths.length / 2)]).split(' ')[0];
  } catch (error) {
    image.src = baseUrl;
  }
}

// Resolves once an off-DOM image is ready to paint without flicker: whichever comes first of `decode()` (preferred —
// guarantees a fully decoded bitmap) or the plain `load`/`error` event. decode() can stall indefinitely in some
// browser states (e.g. a backgrounded/hidden tab), so it must never be awaited on its own.
function waitForImage(image) {
  return new Promise((resolve) => {
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      resolve();
    };

    if (image.decode) image.decode().then(done).catch(() => {});

    if (image.complete) {
      done();
    } else {
      image.addEventListener('load', done, { once: true });
      image.addEventListener('error', done, { once: true });
    }
  });
}

// Parses a trusted-shaped HTML string (already scoped to a specific selector from our own fetch) into a sanitized
// DocumentFragment: strips scripts/styles/forms/iframes/interactive controls, inline event handlers and id attributes.
function sanitizeFragment(html) {
  const template = document.createElement('template');
  template.innerHTML = html;
  const fragment = template.content;

  fragment.querySelectorAll('script, style, form, iframe, button, input, select, textarea').forEach((element) => element.remove());

  fragment.querySelectorAll('*').forEach((element) => {
    Array.from(element.attributes).forEach((attribute) => {
      if (attribute.name === 'id' || attribute.name.toLowerCase().startsWith('on')) {
        element.removeAttribute(attribute.name);
      }
    });
  });

  return fragment;
}

function currencyRate() {
  const rate = parseFloat(window.Shopify?.currency?.rate);
  return Number.isFinite(rate) && rate > 0 ? rate : 1;
}

// Formats cents using the store's own money format string (window.themeVariables.settings.moneyFormat, e.g.
// "₴{{amount_no_decimals}}"), so prices match the rest of the theme exactly instead of relying on a generic
// Intl.NumberFormat guess. Supports the four placeholder variants Shopify themes actually use here; falls back to
// Intl if the format string is missing or unrecognised.
function createMoneyFormatter() {
  const settings = (window.themeVariables && window.themeVariables.settings) || {};
  const formatString = settings.moneyFormat || '';
  const placeholderMatch = formatString.match(/\{\{\s*(\w+)\s*\}\}/);

  if (!placeholderMatch) {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'UAH';
    const locale = document.documentElement.lang || undefined;
    let format;

    try {
      format = new Intl.NumberFormat(locale, { style: 'currency', currency });
    } catch (error) {
      format = new Intl.NumberFormat(undefined, { style: 'currency', currency: 'UAH' });
    }

    return (cents) => format.format(cents / 100);
  }

  const key = placeholderMatch[1];

  return (cents) => formatString.replace(placeholderMatch[0], formatWithDelimiters(cents, key));
}

function formatWithDelimiters(cents, key) {
  let precision = 2;
  let thousands = ',';
  let decimal = '.';

  switch (key) {
    case 'amount_no_decimals':
      precision = 0;
      break;
    case 'amount_with_comma_separator':
      thousands = '.';
      decimal = ',';
      break;
    case 'amount_no_decimals_with_comma_separator':
      precision = 0;
      thousands = '.';
      decimal = ',';
      break;
    case 'amount':
    default:
      break;
  }

  if (isNaN(cents) || cents == null) return '0';

  const number = (cents / 100).toFixed(precision);
  const parts = number.split('.');
  const wholePart = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
  const decimalPart = parts[1] ? decimal + parts[1] : '';

  return wholePart + decimalPart;
}

function cssEscape(value) {
  return window.CSS?.escape ? window.CSS.escape(value) : value.replace(/["\\]/g, '\\$&');
}

if (!window.customElements.get('dtr-kit-builder')) {
  window.customElements.define('dtr-kit-builder', DtrKitBuilder);
}
